import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

interface Availability {
  id: string;
  escort_id: string;
  day: string;
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface AvailabilitySettingsProps {
  escortId?: string;
}

const AvailabilitySettings: React.FC<AvailabilitySettingsProps> = ({ escortId }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [availabilities, setAvailabilities] = useState<Record<string, Availability>>({});
  
  // Jours de la semaine
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  // Heures disponibles (toutes les 30 min)
  const hours = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = (i % 2) * 30;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  });

  // Schéma Zod pour valider une ligne de disponibilité
  const availabilitySchema = z.object({
      start_time: z.string(),
      end_time: z.string(),
      is_available: z.boolean(),
    }).refine(data => {
      // Valider que end_time > start_time uniquement si is_available est true
      if (data.is_available) {
        return data.end_time > data.start_time;
      }
      return true;
    }, {
      message: t('escort.calendar.error_end_time_must_be_after_start'), // Message d'erreur générique pour le refine
      path: ["end_time"], // Lier l'erreur au champ end_time
    });

  // Utiliser useForm pour gérer l'état et la validation de toutes les disponibilités
  // La structure de l'état sera { monday: AvailabilityData, tuesday: AvailabilityData, ... }
  const form = useForm<Record<string, z.infer<typeof availabilitySchema>>>({ 
    // Note: zodResolver ne fonctionne pas bien avec une structure d'objet dynamique comme celle-ci.
    // La validation devra être gérée manuellement ou via le refine du schéma global si nécessaire.
    // Pour l'instant, nous validons au niveau de chaque ligne dans le JSX.
    defaultValues: days.reduce((acc, day) => {
        acc[day] = { start_time: '09:00', end_time: '21:00', is_available: false };
        return acc;
      }, {} as Record<string, z.infer<typeof availabilitySchema>>)
  });

  useEffect(() => {
    if (escortId) {
      loadAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escortId]);

  const loadAvailability = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('escort_availability')
        .select('*')
        .eq('escort_id', escortId);
      
      if (error) throw error;
      
      // Initialiser les valeurs par défaut du formulaire
      const defaultFormValues = days.reduce((acc, day) => {
        const existingData = data?.find(item => item.day === day);
        acc[day] = {
          start_time: existingData?.start_time || '09:00',
          end_time: existingData?.end_time || '21:00',
          is_available: existingData?.is_available || false,
          id: existingData?.id || '',
          day: existingData?.day || day,
          escort_id: existingData?.escort_id || escortId || '',
        } as Availability;
        return acc;
      }, {} as Record<string, Availability>) as Record<string, Availability>;

      form.reset(defaultFormValues);
      // Garder l'état séparé pour les ID car ils ne font pas partie du schéma de validation direct
      setAvailabilities(defaultFormValues);

    } catch (error) {
      console.error('Error loading availability:', error);
      toast({
        title: t('escort.calendar.error_loading_availability'),
        description: t('escort.calendar.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveAvailability = async () => {
    if (!escortId) return;

    // Déclencher la validation pour tous les champs
    const isValid = await form.trigger();
    if (!isValid) {
        toast({ title: t('common.error'), description: t('common.form_errors'), variant: 'destructive' });
        return;
    }
    
    const formData = form.getValues();
    let validationError = false;

    // Validation manuelle supplémentaire (heure fin > heure début)
    for (const day of days) {
       const dayData = formData[day];
       if (dayData.is_available && dayData.end_time <= dayData.start_time) {
          form.setError(`${day}.end_time`, { 
             type: "manual", 
             message: t('escort.calendar.error_end_time_must_be_after_start') 
          });
          validationError = true;
       }
    }

    if (validationError) {
        toast({ title: t('common.error'), description: t('common.form_errors'), variant: 'destructive' });
        return;
    }

    setIsSaving(true);
    try {
      let updateSuccess = true;
      const updatedAvailabilitiesState: Record<string, Availability> = {};

      for (const day of days) {
        const availabilityData = formData[day];
        // Récupérer l'ID depuis l'état séparé ou l'état initial chargé
        const currentId = availabilities[day]?.id;

        const dataToSave = {
           start_time: availabilityData.start_time,
           end_time: availabilityData.end_time,
           is_available: availabilityData.is_available
        };

        let savedData: Availability | null = null;
        let operationError = null;
        
        if (currentId) {
          const { data, error } = await supabase
            .from('escort_availability')
            .update(dataToSave)
            .eq('id', currentId)
            .select()
            .single(); // S'assurer de récupérer l'enregistrement mis à jour
            operationError = error;
            savedData = data;
        } else {
          const { data, error } = await supabase
            .from('escort_availability')
            .insert({ ...dataToSave, escort_id: escortId, day })
            .select()
            .single(); // Récupérer l'enregistrement inséré avec son ID
            operationError = error;
            savedData = data;
        }
          
        if (operationError) {
          console.error(`Error saving availability for ${day}:`, operationError);
          updateSuccess = false;
          // Stocker une version vide ou l'ancienne version en cas d'erreur?
          updatedAvailabilitiesState[day] = availabilities[day]; 
        } else if (savedData) {
            updatedAvailabilitiesState[day] = savedData;
        } else {
            // Cas improbable où data est null sans erreur
            updatedAvailabilitiesState[day] = availabilities[day]; 
        }
      }

      // Mettre à jour l'état global avec les potentiels nouveaux ID
      setAvailabilities(updatedAvailabilitiesState);
      // Mettre à jour le formulaire aussi pour être synchronisé
      form.reset(updatedAvailabilitiesState); 
      
      if(updateSuccess) {
          toast({
            title: t('escort.calendar.availability_updated'),
            description: t('escort.calendar.availability_update_success'),
          });
      } else {
           toast({
            title: t('escort.calendar.error_saving_partial_title'),
            description: t('escort.calendar.error_saving_partial_desc'),
            variant: 'default',
          });
      }

    } catch (error) { // Erreur générale imprévue
      console.error('Error saving availability:', error);
      toast({
        title: t('escort.calendar.error_saving_availability'),
        description: t('escort.calendar.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    // Envelopper dans le composant Form
    <Form {...form}>
    <Card>
      <CardHeader>
        <CardTitle>{t('escort.calendar.availability_settings')}</CardTitle>
        <CardDescription>
          {t('escort.calendar.availability_description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {days.map((day) => (
          <div key={day} className="border rounded-md p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium capitalize">
                {t(`escort.calendar.${day}`)}
              </h3>
              {/* Utiliser FormField pour le Switch */}
              <FormField
                  control={form.control}
                  name={`${day}.is_available`}
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0"> { /* Ajuster layout */} 
                      <FormControl>
                         <Switch
                           id={`available-${day}`}
                           checked={field.value}
                           onCheckedChange={field.onChange}
                         />
                      </FormControl>
                      <FormLabel htmlFor={`available-${day}`} className="!mt-0"> { /* Ajuster layout */} 
                        {field.value
                          ? t('escort.calendar.available')
                          : t('escort.calendar.unavailable')}
                      </FormLabel>
                   </FormItem>
                  )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Utiliser FormField pour start_time */}
               <FormField
                  control={form.control}
                  name={`${day}.start_time`}
                  render={({ field }) => (
                    <FormItem>
                       <FormLabel>{t('escort.calendar.start_time')}</FormLabel>
                       <Select
                          onValueChange={field.onChange}
                          value={field.value} // Utiliser value au lieu de defaultValue
                          disabled={!form.watch(`${day}.is_available`)} // Lire la valeur depuis form
                        >
                          <FormControl>
                             <SelectTrigger>
                                <SelectValue placeholder={t('escort.calendar.select_time')} />
                             </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {hours.map((hour) => (
                              <SelectItem key={`start-${day}-${hour}`} value={hour}>
                                {hour}
                              </SelectItem>
                            ))}
                          </SelectContent>
                       </Select>
                       <FormMessage />
                    </FormItem>
                  )}
               />
              
              {/* Utiliser FormField pour end_time */}
               <FormField
                  control={form.control}
                  name={`${day}.end_time`}
                  render={({ field }) => (
                    <FormItem>
                       <FormLabel>{t('escort.calendar.end_time')}</FormLabel>
                       <Select
                          onValueChange={field.onChange}
                          value={field.value} // Utiliser value au lieu de defaultValue
                          disabled={!form.watch(`${day}.is_available`)} // Lire la valeur depuis form
                        >
                          <FormControl>
                             <SelectTrigger>
                                <SelectValue placeholder={t('escort.calendar.select_time')} />
                             </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {hours.map((hour) => (
                              <SelectItem key={`end-${day}-${hour}`} value={hour}>
                                {hour}
                              </SelectItem>
                            ))}
                          </SelectContent>
                       </Select>
                       {/* Afficher l'erreur spécifique ici */}
                       <FormMessage /> 
                    </FormItem>
                  )}
               />
            </div>
          </div>
        ))}
      </CardContent>
      <CardFooter className="border-t p-6">
         {/* Le bouton est maintenant hors du map */}
         <Button
            className="ml-auto"
            onClick={saveAvailability} // Devrait peut-être utiliser form.handleSubmit(saveAvailability) ?
            disabled={isSaving || !form.formState.isDirty} // Désactiver si pas de changements
            >
            {isSaving 
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.saving')}</> 
                : t('common.save_changes')}
          </Button>
      </CardFooter>
    </Card>
    </Form>
  );
};

export default AvailabilitySettings;
